import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiExternalLink, FiGithub } from "react-icons/fi";
import Chip, { ChipRow } from "../common/Chip";
import CaseNotes from "../common/CaseNotes";
import ArchitectureDialog from "../common/ArchitectureDialog";
import { SNAP, stampIn, staggerContainer } from "../../utils/motion";

// A featured quest. The objective frames these with L-shaped corner brackets,
// which is already a hard-edge device and survives the dell-1996 contract
// unchanged. Thumbnail left, dossier right, actions beneath.

const Card = styled(motion.div)`
  position: relative;
  background: ${({ theme }) => theme.biome.projects.card};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0;
  padding: 28px;

  &:hover {
    border-color: ${({ theme }) => theme.orange};
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

// Four L-brackets, one per corner, each drawn with two borders. They scale in
// from the corner after the card lands, which is what makes the frame read as
// "locking on" rather than just appearing.
const Bracket = styled(motion.span)`
  position: absolute;
  width: 16px;
  height: 16px;
  pointer-events: none;
  border-color: ${({ theme }) => theme.orange};
  border-style: solid;
  border-width: 0;

  ${({ $pos }) =>
    ({
      tl: "top: 8px; left: 8px; border-top-width: 2px; border-left-width: 2px;",
      tr: "top: 8px; right: 8px; border-top-width: 2px; border-right-width: 2px;",
      bl: "bottom: 8px; left: 8px; border-bottom-width: 2px; border-left-width: 2px;",
      br: "bottom: 8px; right: 8px; border-bottom-width: 2px; border-right-width: 2px;",
    }[$pos])}
`;

const Row = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const Thumb = styled.div`
  flex: 0 0 auto;
  width: 190px;
  height: 140px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: #000000;

  /* contain, not cover: these thumbnails range from 1:1 to 16:9, and cover
     was cropping a quarter off each side of the widest ones, cutting through
     the text baked into them. The black ground mats whatever does not fill
     the box. */
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 640px) {
    width: 100%;
    height: 170px;
  }
`;


const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 26px;
  font-weight: 900;
  line-height: 1.05;
  color: ${({ theme }) => theme.biome.projects.ink};
  margin: 0 0 10px;

  @media (max-width: 768px) {
    font-size: 21px;
  }
`;

const Desc = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.biome.projects.muted};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
`;

const Action = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: ${({ theme }) => theme.biome.projects.ink};
  text-decoration: none;

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  transition: background 120ms linear, color 120ms linear;

  &:hover {
    background: ${({ theme }) => theme.orange};
    border-color: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: SNAP, staggerChildren: 0.05, delayChildren: 0.25 },
  },
};

const bracketVariants = {
  hidden: { opacity: 0, scale: 0.3 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: SNAP } },
};

const CORNERS = [
  ["tl", "top left"],
  ["tr", "top right"],
  ["bl", "bottom left"],
  ["br", "bottom right"],
];

const ProjectCards = React.forwardRef(({ project }, ref) => (
  <Card
    ref={ref}
    variants={variants}
  >
    {CORNERS.map(([pos, origin]) => (
      <Bracket
        key={pos}
        $pos={pos}
        variants={bracketVariants}
        style={{ transformOrigin: origin }}
        aria-hidden="true"
      />
    ))}

    <Row>
      {project.image && (
        <Thumb>
          <img src={project.image} alt={project.title} loading="lazy" />
        </Thumb>
      )}

      <Body>
        <Title>{project.title}</Title>
        <Desc>{project.description}</Desc>
        <CaseNotes project={project} />
      </Body>
    </Row>

    {project.tags?.length > 0 && (
      <ChipRow
        as={motion.div}
        style={{ marginTop: 18 }}
        variants={staggerContainer(0.035)}
      >
        {project.tags.map((tag) => (
          <Chip key={tag} as={motion.span} variants={stampIn} $tone="projects">
            {tag}
          </Chip>
        ))}
      </ChipRow>
    )}

    <Actions>
      <ArchitectureDialog project={project} />
      {project.webapp && (
        <Action href={project.webapp} target="_blank" rel="noreferrer">
          <FiExternalLink /> Link
        </Action>
      )}
      {project.github && (
        <Action href={project.github} target="_blank" rel="noreferrer">
          <FiGithub /> Code
        </Action>
      )}
    </Actions>
  </Card>
));

ProjectCards.displayName = "ProjectCards";

export default ProjectCards;
